import {
  Box,
  Grid,
  Toolbar,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Pagination,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../../../utils/Api";
import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import { CloseOutlined } from "@mui/icons-material";

const DamagedRawMaterials = () => {
  const token = localStorage.getItem("token");
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

  const [allRawMaterials, setAllRawMaterials] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [remark, setRemark] = useState("");
  const [quantities, setQuantities] = useState({});

  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const fetchAllRawMaterials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { outletId: outletData?._id, limit: 1000 },
        }
      );

      setAllRawMaterials(response?.data?.data?.totalRecords || []);
      setCategories(response?.data?.data?.rawMaterialCategories || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch raw materials.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllRawMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Unique categories
  const uniqueCategories = useMemo(() => {
    const cats = Array.isArray(categories) ? categories : [];
    return cats.filter(
      (category, index, self) =>
        index ===
        self.findIndex((c) => c?.mainCategory?._id === category?.mainCategory?._id)
    );
  }, [categories]);

  // ✅ Search-only list (like LocalStock)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setRawMaterials([]);
      setCurrentPage(1);
      return;
    }

    let filtered = [...(Array.isArray(allRawMaterials) ? allRawMaterials : [])];

    if (selectedCategory) {
      filtered = filtered.filter(
        (item) => item?.rawMaterial?.mainCategory?._id === selectedCategory
      );
    }

    const q = searchQuery.toLowerCase();
    filtered = filtered.filter((item) =>
      (item?.rawMaterial?.name || "").toLowerCase().includes(q)
    );

    setRawMaterials(filtered);
    setCurrentPage(1);
  }, [allRawMaterials, selectedCategory, searchQuery]);

  const hasSearch = !!searchQuery.trim();
  const hasResults = rawMaterials.length > 0;

  // Pagination slice
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMaterials = rawMaterials.slice(startIndex, endIndex);

  const handlePageChange = (event, page) => setCurrentPage(page);

  // ✅ Fixed stale state issue by using functional updates.
  const handleQuantityChange = (itemId, inputValue) => {
    const numericValue = parseFloat(inputValue);

    setQuantities((prev) => {
      const updated = { ...prev };
      if (!inputValue || isNaN(numericValue) || numericValue <= 0) {
        delete updated[itemId];
      } else {
        updated[itemId] = inputValue;
      }
      return updated;
    });

    setSelectedItems((prev) => {
      const hasValidQty = !isNaN(numericValue) && numericValue > 0;
      if (hasValidQty) return prev.includes(itemId) ? prev : [...prev, itemId];
      return prev.filter((id) => id !== itemId);
    });
  };

  const removeSelectedItem = (itemId) => {
    setSelectedItems((prev) => prev.filter((id) => id !== itemId));
    setQuantities((prev) => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!selectedItems.length) {
      toast.error("Please select at least one item by adding quantities.");
      return;
    }

    if (!remark.trim()) {
      toast.error("Please provide a reason for the transaction.");
      return;
    }

    const requestData = selectedItems.map((itemId) => {
      return {
        rawMaterialId: itemId,
        transferQuantity: Number(quantities[itemId]),
      };
    });

    try {
      const response = await axios.post(
        `${API.BASE_URL}stock-transactions/debitDamageStockFromOutlet`,
        {
          outletId: outletData?._id,
          outletStockTransferData: requestData,
          remark,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response?.data) {
        toast.success("Damaged raw materials logged successfully.");
        setSelectedItems([]);
        setQuantities({});
        setRemark("");
        setSearchQuery("");
        setSelectedCategory("");
        fetchAllRawMaterials();
      } else {
        throw new Error("Submission failed.");
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.error || "Failed to log damaged raw materials.");
    }
  };

  const selectedCount = selectedItems?.length || 0;

  return (
    <Box className="dashBoard" sx={{ minHeight: "100vh" }}>
      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e6eaf0",
                  background: "#fff",
                }}
              >
                <CardContent sx={{ py: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                        Search Raw Material
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Type to search raw materials..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                            background: "#fff",
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchRounded />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                        Category
                      </Typography>
                      <FormControl
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 },
                        }}
                      >
                        <Select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>All</em>
                          </MenuItem>
                          {uniqueCategories?.map((category) => (
                            <MenuItem
                              key={category?.mainCategory?._id}
                              value={category?.mainCategory?._id}
                            >
                              {category?.mainCategory?.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Content */}
              <Card
                elevation={0}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  border: "1px solid #e6eaf0",
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  {loading ? (
                    <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                      <CircularProgress />
                      <Typography sx={{ color: "#334155", fontWeight: 700 }}>
                        Fetching raw materials...
                      </Typography>
                    </Box>
                  ) : !hasSearch ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                        Start by searching above
                      </Typography>
                      <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                        Type at least 1 character to see matching raw materials.
                      </Typography>
                    </Box>
                  ) : paginatedMaterials?.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                        No raw materials found
                      </Typography>
                      <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                        No matches for “{searchQuery}”.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                          <Chip
                            icon={<FilterAltOutlined />}
                            label={`Rows: ${rawMaterials.length}`}
                            variant="outlined"
                            sx={{ fontWeight: 800, borderRadius: 2 }}
                          />
                          <Chip
                            label={`Page: ${currentPage} / ${Math.max(1, Math.ceil(rawMaterials.length / itemsPerPage))}`}
                            variant="outlined"
                            sx={{ fontWeight: 800, borderRadius: 2 }}
                          />
                        </Stack>
                      </Box>

                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          borderRadius: 0,
                          maxHeight: "62vh",
                          overflow: "auto",
                        }}
                      >
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              {["Raw Material", "Available", "Damage Qty"].map((h) => (
                                <TableCell
                                  key={h}
                                  sx={{
                                    fontWeight: 900,
                                    color: "#0f172a",
                                    background: "#f1f5f9",
                                    borderBottom: "1px solid #e2e8f0",
                                    whiteSpace: "nowrap",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                  }}
                                  align={h === "Raw Material" ? "left" : "center"}
                                >
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {paginatedMaterials.map((item, index) => {
                              const itemId = item?.rawMaterial?._id;
                              const isSelected = selectedItems.includes(itemId);
                              const isOutOfStock = Number(item?.currentStock || 0) <= 0;

                              return (
                                <TableRow
                                  key={itemId || index}
                                  hover
                                  sx={{
                                    backgroundColor: isSelected ? "#f8fbff" : index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                    "& td": { borderBottom: "1px solid #f1f5f9" },
                                  }}
                                >
                                  <TableCell align="left">
                                    <Typography
                                      sx={{
                                        fontSize: 14,
                                        fontWeight: 900,
                                        color: "#0f172a",
                                        textTransform: "capitalize",
                                      }}
                                    >
                                      {item?.rawMaterial?.name || "N/A"}
                                    </Typography>

                                    <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                      {item?.rawMaterial?.mainCategory?.name || "—"} • {item?.rawMaterial?.unit || "—"}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="center" sx={{ fontWeight: 800, color: isOutOfStock ? "#b91c1c" : "#0f172a" }}>
                                    {Number(item?.currentStock || 0).toFixed(2)} {item?.rawMaterial?.unit}
                                    {isOutOfStock ? (
                                      <Box sx={{ mt: 0.6 }}>
                                        <Chip
                                          label="Out of Stock"
                                          size="small"
                                          sx={{
                                            fontWeight: 900,
                                            borderRadius: 2,
                                            backgroundColor: "#fdecea",
                                            color: "#b91c1c",
                                            "& .MuiChip-label": { color: "#b91c1c" },
                                          }}
                                        />
                                      </Box>
                                    ) : null}
                                  </TableCell>

                                  <TableCell align="center">
                                    {isOutOfStock ? (
                                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#94a3b8" }}>
                                        —
                                      </Typography>
                                    ) : (
                                      <TextField
                                        size="small"
                                        placeholder="Qty"
                                        value={quantities[itemId] || ""}
                                        onChange={(e) => handleQuantityChange(itemId, e.target.value)}
                                        onKeyDown={(e) => {
                                          const allowed = [
                                            "Backspace",
                                            "Tab",
                                            "ArrowLeft",
                                            "ArrowRight",
                                            "Delete",
                                            "Home",
                                            "End",
                                          ];
                                          if (allowed.includes(e.key)) return;
                                          if (!/[0-9.]/.test(e.key)) e.preventDefault();
                                        }}
                                        sx={{
                                          width: 120,
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            backgroundColor: "#fff",
                                            height: 40,
                                            "&.Mui-focused fieldset": { borderColor: "#003049" },
                                          },
                                        }}
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Divider />

                      {hasResults && (
                        <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                          <Pagination
                            count={Math.ceil(rawMaterials.length / itemsPerPage)}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            variant="outlined"
                            shape="rounded"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* RIGHT: Selected + Remark + Confirm */}
            <Grid item xs={12} md={5}>
              {/* Selected Items */}
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e6eaf0",
                  background: "#fff",
                }}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ mb: 1 }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#0f172a" }}>
                        Selected Items
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.3 }}>
                        Items will appear here when you enter a valid quantity.
                      </Typography>
                    </Box>

                    <Chip
                      icon={<FilterAltOutlined />}
                      label={`${selectedCount} Selected`}
                      variant="outlined"
                      sx={{ fontWeight: 800, borderRadius: 2 }}
                    />
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  {selectedCount === 0 ? (
                    <Box sx={{ py: 3, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                        No items selected yet.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1}>
                      {selectedItems.map((itemId) => {
                        const selectedItem = allRawMaterials.find((i) => i?.rawMaterial?._id === itemId);
                        if (!selectedItem) return null;

                        return (
                          <Box
                            key={itemId}
                            sx={{
                              border: "1px solid #e6eaf0",
                              borderRadius: 2,
                              px: 1.5,
                              py: 1.2,
                              background: "#f8fbff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1.5,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 900,
                                  color: "#0f172a",
                                  textTransform: "capitalize",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {selectedItem?.rawMaterial?.name || "N/A"}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                Qty: <b>{quantities[itemId]}</b> {selectedItem?.rawMaterial?.unit}
                              </Typography>
                            </Box>

                            <IconButton
                              onClick={() => removeSelectedItem(itemId)}
                              sx={{
                                color: "#0f172a",
                                border: "1px solid #e6eaf0",
                                borderRadius: 2,
                                "&:hover": { background: "#fff" },
                              }}
                              size="small"
                            >
                              <CloseOutlined fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Card>

              {/* Reason */}
              <Card
                elevation={0}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  border: "1px solid #e6eaf0",
                  background: "#fff",
                }}
              >
                <CardContent>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Reason for Damage / Wastage
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter reason (required)"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" },
                    }}
                  />
                </CardContent>
              </Card>

              {/* Confirm */}
              <Card
                elevation={0}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  border: "1px solid #e6eaf0",
                  background: "#fff",
                }}
              >
                <CardContent>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={!selectedItems.length || loading}
                    sx={{
                      height: 52,
                      borderRadius: 2,
                      fontWeight: 900,
                      textTransform: "none",
                      backgroundColor: "#003049",
                      "&:hover": { backgroundColor: "#00263a" },
                      "&.Mui-disabled": { backgroundColor: "#b0bec5", color: "#ffffff" },
                    }}
                  >
                    {loading ? "Processing..." : "Confirm Damage"}
                  </Button>

                  <Typography sx={{ fontSize: 11, color: "#64748b", mt: 1, textAlign: "center" }}>
                    Make sure quantities are correct before confirming.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DamagedRawMaterials;
